import { LocalDate } from "@js-joda/core";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { Modal } from "components/designV2/modal/modal";
import { CustomIcon } from "components/icon/customIcon.component";
import type { CompanyCustomer, CompanyCustomerProduct } from "model/Company";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  InvoiceActions,
  InvoiceCustomerSummary,
  InvoiceErrorText,
  InvoiceField,
  InvoiceFieldRow,
  InvoiceFiltersBody,
  InvoiceModalContent,
  InvoiceResultsDescription,
  InvoiceResultsHeader,
  InvoiceResultsTitle,
  InvoiceSection,
  InvoiceSectionHeader,
  InvoiceSectionSummary,
  InvoiceSectionTitle,
  InvoiceTable,
  InvoiceTableRow,
  InvoiceTotalBlock,
  InvoiceTotalLabel,
  InvoiceTotalValue,
  InvoiceTotalsRow,
  ProductOptionRow,
  ProductOptionsList,
  ProductSelector,
  ProductSelectorActions,
  ProductSelectorLabel,
  ProductSelectorPanel,
  ProductSelectorTrigger,
  ProductSelectorValue,
} from "./customerInvoiceModal.styled";
import { useCustomerInvoiceData } from "./useCustomerInvoiceData.hook";

type Props = {
  companyId?: number;
  customer: CompanyCustomer | null;
  setShowCustomerInvoiceModal: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomerInvoiceModal: boolean;
};

const formatLocalDate = (date: LocalDate): string =>
  `${date.year()}-${String(date.monthValue()).padStart(2, "0")}-${String(
    date.dayOfMonth(),
  ).padStart(2, "0")}`;

const formatRangeSummary = (dates: readonly LocalDate[]): string => {
  if (dates.length === 0) return "";
  return `${formatLocalDate(dates[0])} - ${formatLocalDate(
    dates[dates.length - 1],
  )}`;
};

const toCheckedState = (checked: boolean): "checked" | "unchecked" =>
  checked ? "checked" : "unchecked";

export const CustomerInvoiceModal: React.FC<Props> = ({
  companyId,
  customer,
  setShowCustomerInvoiceModal,
  showCustomerInvoiceModal,
}) => {
  const { t } = useTranslation();
  const [customerProducts, setCustomerProducts] = useState<
    readonly CompanyCustomerProduct[]
  >([]);
  const [selectedCustomerProducts, setSelectedCustomerProducts] = useState<
    readonly string[]
  >([]);
  const [selectedDates, setSelectedDates] = useState<readonly LocalDate[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [productsOpen, setProductsOpen] = useState(false);
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [excludeWeekends, setExcludeWeekends] = useState(false);
  const {
    getInvoice,
    hasError,
    hasFetchedInvoice,
    invoiceRows,
    isLoadingInvoice,
    resetInvoice,
  } = useCustomerInvoiceData();

  useEffect(() => {
    const products = customer?.agreements?.[0]?.products ?? [];
    setCustomerProducts(products);
  }, [customer]);

  const selectedProductsSummary = useMemo((): string => {
    if (selectedCustomerProducts.length === 0) {
      return t("CompanyCustomers.NotChosen");
    }

    const selectedTitles = customerProducts
      .filter((product) => {
        if (!product.id) return false;
        return selectedCustomerProducts.includes(product.id);
      })
      .map((product) => product.title);

    if (selectedTitles.length <= 2) {
      return selectedTitles.join(", ");
    }

    return `${selectedTitles.slice(0, 2).join(", ")} +${
      selectedTitles.length - 2
    }`;
  }, [customerProducts, selectedCustomerProducts, t]);

  const filtersSummary = useMemo((): string => {
    if (selectedDates.length === 0) return "";
    return formatRangeSummary(selectedDates);
  }, [selectedDates]);

  const totalHours = useMemo(
    (): number =>
      invoiceRows.reduce(
        (sum, invoiceRow) => sum + invoiceRow.totalWorkHours,
        0,
      ),
    [invoiceRows],
  );

  const totalPrice = useMemo(
    (): number =>
      invoiceRows.reduce((sum, invoiceRow) => sum + invoiceRow.totalPrice, 0),
    [invoiceRows],
  );

  const toggleProduct = (productId: string): void => {
    setSelectedCustomerProducts((currentValues) =>
      currentValues.includes(productId)
        ? currentValues.filter((value) => value !== productId)
        : [...currentValues, productId],
    );
  };

  const selectAllProducts = (): void => {
    setSelectedCustomerProducts(
      customerProducts
        .map((product) => product.id)
        .filter((productId): productId is string => Boolean(productId)),
    );
  };

  const clearProductSelection = (): void => {
    setSelectedCustomerProducts([]);
  };

  const onClose = (): void => {
    setShowCustomerInvoiceModal(false);
    setSelectedCustomerProducts([]);
    setSelectedDates([]);
    setFiltersOpen(true);
    setProductsOpen(false);
    setExcludeHolidays(false);
    setExcludeWeekends(false);
    resetInvoice();
  };

  return (
    <Modal
      isOpen={showCustomerInvoiceModal}
      onClose={onClose}
      label={t("CompanyCustomers.CreateInvoice")}
      size="medium"
    >
      <InvoiceModalContent>
        {customer && (
          <InvoiceSection>
            <InvoiceSectionTitle>
              {t("CompanyCustomers.CustomerInformation")}
            </InvoiceSectionTitle>
            <InvoiceCustomerSummary>
              <LabeledValue
                label={t("CompanyCustomers.Name")}
                value={customer.name}
                variant="small"
              />
              <LabeledValue
                label={t("CompanyCustomers.OrganizationNumber")}
                value={customer.organizationNumber}
                variant="small"
              />
              <LabeledValue
                label={t("CompanyCustomers.Contact")}
                value={customer.contact}
                variant="small"
              />
            </InvoiceCustomerSummary>
          </InvoiceSection>
        )}

        <InvoiceSection>
          <InvoiceSectionHeader
            type="button"
            $open={filtersOpen}
            onClick={() => setFiltersOpen((currentValue) => !currentValue)}
          >
            <div>
              <InvoiceSectionTitle>
                {t("CompanyCustomers.ChooseInformationForInvoice")}
              </InvoiceSectionTitle>
              {!filtersOpen && filtersSummary && (
                <InvoiceSectionSummary>{filtersSummary}</InvoiceSectionSummary>
              )}
            </div>
            <CustomIcon
              name={filtersOpen ? "v2/up-chevron" : "v2/down-chevron"}
              size="20px"
            />
          </InvoiceSectionHeader>

          {filtersOpen && (
            <InvoiceFiltersBody>
              <InvoiceFieldRow>
                <InvoiceField>
                  <DatePicker
                    label={`${t("CreateShift.Date")}*`}
                    value={selectedDates[0] ?? null}
                    onRangeChange={setSelectedDates}
                    allowRangePicker
                    rangeOnly
                    dateFormat="YYYY-MM-DD"
                    showExcludeToggles
                    excludeHolidays={excludeHolidays}
                    onExcludeHolidaysChange={setExcludeHolidays}
                    excludeWeekends={excludeWeekends}
                    onExcludeWeekendsChange={setExcludeWeekends}
                  />
                </InvoiceField>

                <InvoiceField>
                  <ProductSelector>
                    <ProductSelectorLabel>
                      {t("CompanyCustomers.Products")}*
                    </ProductSelectorLabel>
                    <ProductSelectorTrigger
                      type="button"
                      $isOpen={productsOpen}
                      onClick={() =>
                        setProductsOpen((currentValue) => !currentValue)
                      }
                    >
                      <ProductSelectorValue
                        $placeholder={selectedCustomerProducts.length === 0}
                      >
                        {selectedProductsSummary}
                      </ProductSelectorValue>
                      <CustomIcon
                        name={
                          productsOpen ? "v2/up-chevron" : "v2/down-chevron"
                        }
                        size="20px"
                      />
                    </ProductSelectorTrigger>

                    {productsOpen && (
                      <ProductSelectorPanel>
                        <ProductSelectorActions>
                          <SecondaryButton
                            label={t("General.ChooseAll")}
                            size="small"
                            onClick={selectAllProducts}
                            type="button"
                          />
                          <SecondaryButton
                            label={t("General.ClearAll")}
                            size="small"
                            onClick={clearProductSelection}
                            type="button"
                          />
                        </ProductSelectorActions>

                        <ProductOptionsList>
                          {customerProducts.map((product) => {
                            const productId = product.id;
                            if (!productId) return null;

                            const isChecked =
                              selectedCustomerProducts.includes(productId);

                            return (
                              <ProductOptionRow key={productId}>
                                <Checkbox
                                  checked={toCheckedState(isChecked)}
                                  onChange={() => toggleProduct(productId)}
                                  label={product.title}
                                />
                              </ProductOptionRow>
                            );
                          })}
                        </ProductOptionsList>
                      </ProductSelectorPanel>
                    )}
                  </ProductSelector>
                </InvoiceField>
              </InvoiceFieldRow>

              {hasError && (
                <InvoiceErrorText>
                  {t("General.ErrorOccurred")}
                </InvoiceErrorText>
              )}

              <InvoiceActions>
                <PrimaryButton
                  label={t("CompanyCustomers.GetInvoiceInformation")}
                  onClick={async () => {
                    const wasFetched = await getInvoice({
                      companyId,
                      selectedDates,
                      selectedProductIds: selectedCustomerProducts,
                    });

                    if (wasFetched) {
                      setFiltersOpen(false);
                    }
                  }}
                  disabled={selectedDates.length === 0}
                  isLoading={isLoadingInvoice}
                  type="button"
                />
              </InvoiceActions>
            </InvoiceFiltersBody>
          )}
        </InvoiceSection>

        {(invoiceRows.length > 0 || hasFetchedInvoice) && (
          <InvoiceSection>
            <InvoiceResultsHeader>
              <InvoiceResultsTitle>
                {t("CompanyCustomers.GetInvoiceInformation")}
              </InvoiceResultsTitle>
              <InvoiceResultsDescription>
                {invoiceRows.length > 0
                  ? t("CompanyCustomers.ChooseInformationForInvoice")
                  : t("General.None")}
              </InvoiceResultsDescription>
            </InvoiceResultsHeader>

            {invoiceRows.length > 0 ? (
              <>
                <InvoiceTable>
                  {invoiceRows.map((invoiceRow) => (
                    <InvoiceTableRow key={invoiceRow.gigId}>
                      <LabeledValue
                        label={t("CompanyCustomers.Name")}
                        value={invoiceRow.workerName ?? "-"}
                        variant="small"
                        truncate
                      />
                      <LabeledValue
                        label={t("CompanyCustomers.Product")}
                        value={invoiceRow.product}
                        variant="small"
                        truncate
                      />
                      <LabeledValue
                        label={t("CompanyCustomers.Price")}
                        value={String(invoiceRow.hourlyRate)}
                        variant="small"
                      />
                      <LabeledValue
                        label={t("CompanyCustomers.TotalHours")}
                        value={String(invoiceRow.totalWorkHours)}
                        variant="small"
                      />
                      <LabeledValue
                        label={t("CompanyCustomers.TotalPrice")}
                        value={String(invoiceRow.totalPrice)}
                        variant="small"
                      />
                    </InvoiceTableRow>
                  ))}
                </InvoiceTable>

                <InvoiceTotalsRow>
                  <InvoiceTotalBlock>
                    <InvoiceTotalLabel>
                      {t("CompanyCustomers.Total")}
                    </InvoiceTotalLabel>
                    <InvoiceTotalValue>{invoiceRows.length}</InvoiceTotalValue>
                  </InvoiceTotalBlock>
                  <InvoiceTotalBlock>
                    <InvoiceTotalLabel>
                      {t("CompanyCustomers.TotalHours")}
                    </InvoiceTotalLabel>
                    <InvoiceTotalValue>{totalHours}</InvoiceTotalValue>
                  </InvoiceTotalBlock>
                  <InvoiceTotalBlock>
                    <InvoiceTotalLabel>
                      {t("CompanyCustomers.TotalPrice")}
                    </InvoiceTotalLabel>
                    <InvoiceTotalValue>
                      {totalPrice} SEK {t("CompanyCustomers.ExcludingVAT")}
                    </InvoiceTotalValue>
                  </InvoiceTotalBlock>
                </InvoiceTotalsRow>
              </>
            ) : (
              <InvoiceResultsDescription>
                {t("General.None")}
              </InvoiceResultsDescription>
            )}
          </InvoiceSection>
        )}
      </InvoiceModalContent>
    </Modal>
  );
};
